import { Suspense, lazy } from 'react';
import localeRoutes from './routes.json';

// import all the skeletons
import { ShoppingBagSkeleton } from '../components/shopping-bag/skeleton';
import CheckoutShippingSkeleton from '../components/checkout-shipping/checkout-shipping-skeleton';
import CheckoutPaymentSkeleton from '../components/checkout-payment/checkout-payment-skeleton';
import OrderConfirmationSkeleton from '../components/order-confirmation/order-confirmation-skeleton';
import LayoutSkeleton from '../components/shared/sections/my-account/layout/layout-skeleton';

// lazy load all the top level components
const ShoppingBag = lazy(() => import('../components/shopping-bag'));
const CheckoutShipping = lazy(() => import('../components/checkout-shipping'));
const CheckoutPayment = lazy(() => import('../components/checkout-payment'));
const OrderConfirmation = lazy(() => import('../components/order-confirmation'));
const MyAccount = lazy(() => import('../components/my-account'));
const MyOrders = lazy(() => import('../components/my-orders'));
const OrderDetails = lazy(() => import('../components/order-details'));
const DummyComponents = lazy(() => import('../components/dummy-components'));
const CustomerPreferance = lazy(() => import('../components/customer-preferance'));
const Gdpr = lazy(() => import('../components/gdpr'));
const Activation = lazy(() => import('../components/activation'));
const ArtworkComponent = lazy(() => import('../components/shared/sections/artwork-details-component'));

const {
    shoppingBag,
    checkoutShipping,
    checkoutPayment,
    orderConfirmation,
    myAccount,
    myOrders,
    orderDetails,
    orderDetailsId,
    customerPreferance,
    gdpr,
    activation,
    pccArtwork
} = localeRoutes;

const shoppingBagPageRoutes = shoppingBag.map((shoppingBagRoutes) => {
    return {
        path: shoppingBagRoutes.path,
        region: shoppingBagRoutes.region,
        title: shoppingBagRoutes.title,
        component: (props) => (
            <Suspense fallback={<ShoppingBagSkeleton />}>
                <ShoppingBag {...props} />
            </Suspense>
        ),
    };
});

const pccArtworkPageRoutes = pccArtwork.map((pccArtworkRoutes) => {
    return {
        path: pccArtworkRoutes.path,
        region: pccArtworkRoutes.region,
        title: pccArtworkRoutes.title,
        component: (props) => (
            <Suspense fallback={<></>}>
                <ArtworkComponent {...props} />
            </Suspense>
        ),
    };
});

const checkoutShippingPageRoutes = checkoutShipping.map(
    (checkoutShippingRoutes) => {
        return {
            path: checkoutShippingRoutes.path,
            region: checkoutShippingRoutes.region,
            title: checkoutShippingRoutes.title,
            component: (props) => (
                <Suspense fallback={<CheckoutShippingSkeleton />}>
                    <CheckoutShipping {...props} />
                </Suspense>
            ),
        };
    }
);

const checkoutPaymentPageRoutes = checkoutPayment.map(
    (checkoutPaymentRoutes) => {
        return {
            path: checkoutPaymentRoutes.path,
            region: checkoutPaymentRoutes.region,
            title: checkoutPaymentRoutes.title,
            component: (props) => (
                <Suspense fallback={<CheckoutPaymentSkeleton />}>
                    <CheckoutPayment {...props} />
                </Suspense>
            ),
        };
    }
);

const orderConfirmationPageRoutes = orderConfirmation.map(
    (orderConfirmationRoutes) => {
        return {
            path: orderConfirmationRoutes.path,
            region: orderConfirmationRoutes.region,
            title: orderConfirmationRoutes.title,
            component: (props) => (
                <Suspense fallback={<OrderConfirmationSkeleton />}>
                    <OrderConfirmation {...props} />
                </Suspense>
            ),
        };
    }
);

const myAccountRoutes = myAccount.map((myAccountRoutes) => {
    const queryString = window?.location?.search;
    let myAccountSection = queryString === '?id=to-address-list' ? 'address' : 'dashboard';

    return {
        path: myAccountRoutes.path,
        region: myAccountRoutes.region,
        title: myAccountRoutes.title,
        component: (props) => (
            <Suspense fallback={<LayoutSkeleton myAccountSection={myAccountSection} />}>
                <MyAccount {...props} />
            </Suspense>
        ),
    };
});

const myOrdersRoutes = myOrders.map((myOrdersRoutes) => {
    return {
        path: myOrdersRoutes.path,
        region: myOrdersRoutes.region,
        title: myOrdersRoutes.title,
        component: (props) => (
            <Suspense fallback={<></>}>
                <MyOrders {...props} />
            </Suspense>
        ),
    };
});

const orderDetailsRoutes = orderDetails.map((orderDetailsRoutes) => {
    return {
        path: orderDetailsRoutes.path,
        region: orderDetailsRoutes.region,
        title: orderDetailsRoutes.title,
        component: (props) => (
            <Suspense fallback={<></>}>
                <OrderDetails {...props} />
            </Suspense>
        ),
    };
});

const orderDetailsRoutesById = orderDetailsId.map((orderDetailsRoutesById) => {
    return {
        path: orderDetailsRoutesById.path,
        region: orderDetailsRoutesById.region,
        title: orderDetailsRoutesById.title,
        component: (props) => (
            <Suspense fallback={<></>}>
                <OrderDetails {...props} />
            </Suspense>
        ),
    };
});

const customerPreferancePageRoutes = customerPreferance.map((customerPreferanceRoutes) => {
    return {
        path: customerPreferanceRoutes.path,
        region: customerPreferanceRoutes.region,
        title: customerPreferanceRoutes.title,
        component: (props) => (
            <Suspense fallback={<></>}>
                <CustomerPreferance {...props} />
            </Suspense>
        ),
    }
})

const gdprPageRoutes = gdpr.map((gdprRoutes) => {
    return {
        path: gdprRoutes.path,
        region: gdprRoutes.region,
        title: gdprRoutes.title,
        component: (props) => (
            <Suspense fallback={<></>}>
                <Gdpr {...props} />
            </Suspense>
        ),
    }
})

const activationRoutes = activation.map((activationRoutes) => {
    return {
        path: activationRoutes.path,
        region: activationRoutes.region,
        title: activationRoutes.title,
        component: (props) => (
            <Suspense fallback={<></>}>
                <Activation {...props} />
            </Suspense>
        ),
    }
})

const routes = [
    ...shoppingBagPageRoutes,
    ...checkoutShippingPageRoutes,
    ...checkoutPaymentPageRoutes,
    ...orderConfirmationPageRoutes,
    ...myAccountRoutes,
    ...myOrdersRoutes,
    ...orderDetailsRoutes,
    ...customerPreferancePageRoutes,
    ...gdprPageRoutes,
    ...activationRoutes,
    ...orderDetailsRoutesById,
    ...pccArtworkPageRoutes,
    {
        region: 'ie',
        path: 'dummy-components',
        title: 'Dummy Component',
        component: (props) => (
            <Suspense fallback={<div>Loading...</div>}>
                <DummyComponents {...props} />
            </Suspense>
        ),
    },
];

export default routes;
